import { ActionButtonCell, BadgeCell, DatetimeCell } from '../components/DataTable';
import { generatePath } from 'react-router-dom';
import { Routes } from '../Routes';
import { getPermission } from '../hooks';
import { Permissions } from '../Permissions';
import { faFolderOpen, faEdit, faCheckSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import gql from 'graphql-tag';
import { ParameterStatusUtil } from '../api';
import { ReleaseParameterModal } from '../components/Common/Modals';
import { ParameterPreviewModal } from '../components/Parameter';
import { StatusFormValues } from '../components/Forms';
import { FormGroup, Input } from 'reactstrap';
import { Role } from '../Roles';
import { DocumentNode } from 'graphql';
import { ParameterFilter, ParameterStatus } from '../generated/types';

class ParameterGraphQLServiceImpl {

  public getFilter(role: string, name: string, code: string, dimensionCode: string, stoffCode: string, nichtstofflichCode: string, 
    einheitCode: string, probenvorbehandlungCode: string, methodenCode: string, matrixCode: string, sonstigesCode: string, status: StatusFormValues): ParameterFilter {

    
    const filter: ParameterFilter  = {};

    if(name) {
      filter.nameOrAlternativnameContains = name;
    }

    if(code) {
      filter.codeContains = code;
    }
    
    if(dimensionCode) {
      filter.dimension = dimensionCode;
    }
    if(stoffCode) {
      filter.stoff = stoffCode;
    }
    if(nichtstofflichCode) {
      filter.nichtstofflich = nichtstofflichCode;
    }
    if(einheitCode) {
      filter.einheit = einheitCode;
    }
    if(probenvorbehandlungCode) {
      filter.probenvorbehandlung = probenvorbehandlungCode;
    }


    if(methodenCode) {
      filter.methode = methodenCode;
    }
    if(matrixCode) {
      filter.matrix = matrixCode;
    }
    if(sonstigesCode) {
      filter.sonstigesKriterium = sonstigesCode;
    }

    // @ts-ignore
    if(status && status.length > 0) {
      //@ts-ignore
      filter.status = status.map(e => e.value);
    }

    return filter;
  }

  /**
   * 
   */
  public getFilterDuplicates(codeList: string[]): ParameterFilter {
    const filter: ParameterFilter = {
      code: codeList

    }
    return filter;
  }

  public getParameterList(): DocumentNode {
 
    return gql`
    query PARAMETER_PAGE($number: Int!, $size: Int!, $filter: ParameterFilter, $sort: [Sort]) {
      parameter (page: {number: $number, size: $size},
        filter: $filter, sort: $sort) {
        data {
          id
          code
          name
          status
          createdDate
          lastModifiedDate
          stoff {
            name
          }
          nichtstofflich {
            name
          }
          einheit {
            name
            dimension {
              name
            }
          }
          probenvorbehandlung {
            name
          }
          methoden {
            id
            name
            titel
          }
          sonstigeKriterien {
            id
            name
          }
          matrizen {
            id 
            name
          }
        },
        page  { number, size, totalElements, totalPages}
      }
    }
    `;
  }

  public getParameterDetails(): any {
    return gql`
      query Parameter($id: ID!) {
        einParameter(id: $id) {
          
          id
          code
          name
          alternativnamen {
            name
          }
          
          stoff {
            id
            casNummer
            name
          }
          nichtstofflich {
            id
            code
            name
          }
          einheit {
            id 
            code
            symbol
            name
            dimension {
              id
              code
              symbol
              name
            }
          }
          probenvorbehandlung {
            id
            code
            name
          }
          matrizen {
            name
            id
          }
    			sonstigeKriterien {
            id
            name
          }
    			methoden {
            id
            titel
            name
          }
          anforderung {
            email
            anmerkung
            zweckOderQuelle
          }
          status
        }
      }
    `;
  }


  public getColumns(role: string, duplicates: boolean): any {
    return [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }) => {
          return (
            <FormGroup check inline className="pl-3 pt-2">
              <Input type="checkbox" aria-label="Alle Auswählen" {...getToggleAllRowsSelectedProps({ title: 'Alle Datensätze auf Seite auswählen'})} />
              <span className="sr-only">Alle Auswählen</span>
            </FormGroup>
          );
        },
        Cell: ({ row }) => {
          const disabled = row.original.status === ParameterStatusUtil.PS3.id || row.original.status === ParameterStatusUtil.PS2A.id;
          return ( 
            <FormGroup check inline className="pl-3 pt-2">
              <Input type="checkbox" aria-label="Zeile auswählen" {...row.getToggleRowSelectedProps({ title: 'Zeile auswählen'})} disabled={disabled}  />
            </FormGroup>
          );
        }
      },
      ...this.getActionColumns(role, duplicates),
      { Header: "Parametercode", 
        accessor: 'code',
        Cell: ({row}) => {
          if (row.original.status !== ParameterStatusUtil.PS1.id || role === Role.EHPC_RW) {
            return row.original.code;
          }
          return '';
        }
      },
      { Header: "Parametername", accessor: 'name' },
      { Header: "Dimension", disableSortBy: true, accessor: 'einheit.dimension.name' },
      { Header: "Stoff", accessor: 'stoff.name' },
      { Header: "Nichtstoffliche Kenngröße", accessor: 'nichtstofflich.name' },
      { Header: "Einheit", accessor: 'einheit.name' },
      { Header: "Probevorbehandlung", accessor: 'probenvorbehandlung.name' },
      { Header: "Methode",
        disableSortBy: true,
        accessor: 'methoden',
        Cell: ({ row, column}) => {
          return (
            <BadgeCell  row={row} column={column} />
          );}
      },
      { Header: "Sonstiges Kriterium",
        disableSortBy: true,
        accessor: 'sonstigeKriterien',
        Cell: ({ row, column}) => {
          return (
            <BadgeCell  row={row} column={column} />
          );}
      },
      { Header: "Matrix",
        disableSortBy: true,
        accessor: 'matrizen',
        Cell: ({ row, column}) => {
          return (
            <BadgeCell  row={row} column={column} />
          );} },
      { Header: 'Status', id: 'status', accessor: (d: any) => ParameterStatusUtil.getStatus(d.status)?.name },
      {
        Header: 'erstellt am', 
        accessor: 'createdDate',
        Cell: ({row, column}) => { return <DatetimeCell row={row} column={column} dateFormat="" timeFormat="" /> }
      },
      {
        Header: 'zuletzt geändert', 
        accessor: 'lastModifiedDate',
        Cell: ({row, column}) => { return <DatetimeCell row={row} column={column} dateFormat="" timeFormat="" /> }
      }
    ];
  }


  /*
  */
  public getActionColumns(role: string, duplicates: boolean): any {
    const permParamRead = getPermission(role, [Permissions.ParameterRead]);
    const permParamUpdate = getPermission(role, [Permissions.ParameterKomponenteUpdate]);
    const permParamRelease = getPermission(role, [Permissions.ParameterKomponenteRelease]);
    const permParamReject = getPermission(role, [Permissions.ParameterKomponenteDelete]);
    const actionColumns: any = [];

    if (duplicates) {
      return [{
        id: 'action',
        Header: 'Aktion',
        Cell: ({row, column}) => {
          return (
            <ActionButtonCell row={row} column={column} icon={faFolderOpen}
              tooltip="Ansehen"
              modal={(open: boolean, setOpen: Function, element: any) => 
                <ParameterPreviewModal
                  open={open} setOpen={setOpen}
                  element={element}
                  headerId="modal_header_parameter_preview" />}
            />
          );
        }
      }]
    }

    if(permParamRead) {
      actionColumns.push( { 
        id: 'action-open',
        Header: <span className="sr-only">Aktion Ansehen</span>,
        Cell: ({ row, column}) => {
          return (
            <ActionButtonCell row={row} column={column} icon={faFolderOpen}
              tooltip="Ansehen"
              to={generatePath(Routes.ParameterRead, {parameterId: row.original.id })} />
          );}
      });
    }

    if (permParamUpdate) {
      actionColumns.push({ 
        id: 'action-edit',
        Header: <span className="sr-only">Aktion Bearbeiten</span>,
        Cell: ({ row, column}) => {
          if(row.original.status === ParameterStatusUtil.PS3.id) {
            return null;
          }
          return (
            <ActionButtonCell  row={row} column={column} icon={faEdit}
              tooltip="Bearbeiten"
              disabled={(role !== Role.EHPC_RW)}
              to={generatePath(Routes.ParameterEdit, {parameterId: row.original.id  })} />
          );}
      });
    }

    if (permParamRelease) {
      actionColumns.push({ 
        id: 'action-release',
        Header: <span className="sr-only">Aktion Freigeben</span>,
        Cell: ({ row, column}) => {
          if (row.original.status === ParameterStatusUtil.PS1.id) {
            return (
              <ActionButtonCell  row={row} column={column} color="release" icon={faCheckSquare}
                tooltip="Freigeben"
                modal={(open: boolean, setOpen: Function, element: any) => <ReleaseParameterModal open={open} setOpen={setOpen} items={[element]} />}
              />
            );
          }
          else {
            return null;
          }
        }
      });
    }

    if (permParamReject) {
      actionColumns.push({ 
        id: 'action-delete',
        Header: <span className="sr-only">Aktion Ablehnen</span>,
        Cell: ({ row, column}) => {
          if (row.original.status !== ParameterStatusUtil.PS1.id) {
            return null;
          }
          return (
            <ActionButtonCell  row={row} column={column} color="delete" icon={faTrash}
              tooltip="Ablehnen"
              to={generatePath(Routes.ParameterReject, {parameterId: row.original.id  })} />
          );}
      });
    }

    return [{
      Header: 'Aktionen',
      columns: actionColumns,
    }];
  }

}

export const ParameterGraphQLService = new ParameterGraphQLServiceImpl();
